export const PARENT_SITE = "https://softlinetechs.com/";

export const LOGOS = {
  nigeria: "/images/softline-nigeria-logo-white.svg",
  nigeriaDark: "/images/softline-nigeria-logo.svg",
  softebox: "/images/softebox-logo.jpeg",
  myid: "/images/myid-logo.jpeg",
  cofundapp: "/images/cofundapp-logo.jpeg",
};

export const IMG = {
  hero: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=80",
  lagos: "https://images.unsplash.com/photo-1569706971306-de5d78f6418e?auto=format&fit=crop&w=1200&q=80",
  team: "https://images.unsplash.com/photo-1573164574397-dd250bc8a598?auto=format&fit=crop&w=1200&q=80",
  servers: "https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?auto=format&fit=crop&w=1200&q=80",
  identity: "https://images.unsplash.com/photo-1636793298494-a79e1998d6a9?auto=format&fit=crop&w=1200&q=80",
};

export const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    to: "/about",
    children: [
      { label: "About Us", to: "/about", desc: "Who we are and what we do" },
      { label: "Our Group", to: "/about/our-group", desc: "Softline Technologies and Services AB, Sweden" },
    ],
  },
  {
    label: "Products",
    to: "/products",
    children: [
      { label: "Products Overview", to: "/products", desc: "All Softline digital products" },
      { label: "SoftEbox", to: "/products/softebox", desc: "Secure digital mailbox and document services" },
      { label: "myID", to: "/products/myid", desc: "Digital identity and authentication" },
      { label: "CoFundApp", to: "/products/cofundapp", desc: "Structured community savings technology" },
    ],
  },
  {
    label: "Services",
    to: "/services",
    children: [
      { label: "Services Overview", to: "/services", desc: "Enterprise technology services" },
      { label: "Solution Architecture", to: "/services/solution-architecture" },
      { label: "Cloud & Infrastructure", to: "/services/cloud-infrastructure" },
      { label: "Cybersecurity", to: "/services/cybersecurity" },
      { label: "Software Engineering", to: "/services/software-engineering" },
      { label: "Systems Integration", to: "/services/systems-integration" },
      { label: "Data & Digital Transformation", to: "/services/data-digital-transformation" },
    ],
  },
  { label: "Industries", to: "/industries" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export const STATUS = {
  key: "MVP",
  label: "MVP — Continuous Improvement",
  cta: "Request a Preview",
  note:
    "Available for preview engagements. Capabilities are being continuously improved with early customers.",
};

export const CONTACT_CATEGORIES = [
  "Enterprise Consulting",
  "Product Demonstration",
  "Technology Partnership",
  "Systems Integration",
  "General Enquiry",
];

export const PRODUCTS = [
  {
    slug: "softebox",
    name: "SoftEbox",
    tagline: "Trusted Digital Mailbox",
    short: "Secure digital mailbox and document services — deliver, sign, track and archive critical business documents.",
    desc: "SoftEbox is a secure digital mailbox and document services platform that connects verified business senders with recipients through a trusted digital mailbox — replacing print, courier and untracked email attachments with structured, auditable delivery.",
    logo: LOGOS.softebox,
    logoBg: "#eef2f7",
    accent: "#1e5bb8",
    useCase: "Banks, utilities and public agencies delivering statements, policies and notices that must provably arrive.",
    problem:
      "Critical documents — invoices, policies, statements, contracts and statutory notices — still move through print, courier and untracked email. Businesses have no proof of delivery, recipients lose documents across channels, and retrieving an archived copy can take days.",
    overview:
      "SoftEbox gives every organisation a business sender portal and every recipient a secure digital mailbox. Documents are published once, delivered to verified recipients, tracked through their lifecycle, and archived with an audit trail — through the portal or programmatically via API.",
    how: [
      "The organisation onboards as a verified business sender in the SoftEbox portal.",
      "Documents are uploaded or pushed via API and addressed to recipient mailboxes.",
      "Recipients are notified and read, download, sign or act on the document.",
      "Delivery, opening and signature status is tracked at every step.",
      "Completed documents and their audit trails are archived and exportable.",
    ],
    capabilities: [
      { title: "Secure document delivery", desc: "Structured, tracked delivery of business documents to verified recipients.", status: "available" },
      { title: "Business sender portal", desc: "Compose, address and manage outbound document campaigns.", status: "available" },
      { title: "Recipient digital mailbox", desc: "A single secure mailbox where recipients receive and manage their documents.", status: "available" },
      { title: "Document management & archive", desc: "Searchable archive with retention-friendly organisation.", status: "available" },
      { title: "Digital signature workflows", desc: "Request and collect signatures on delivered documents.", status: "available" },
      { title: "API integration", desc: "REST API for high-volume dispatch from existing business systems.", status: "available" },
      { title: "Delivery tracking & auditability", desc: "Per-document status events and exportable audit trails.", status: "available" },
      { title: "Invoice & payment-linked workflows", desc: "Payment-related document journeys connected to collections processes.", status: "roadmap" },
    ],
    integration:
      "SoftEbox exposes a REST API for document dispatch and status retrieval, and is designed to integrate with core business systems, ERPs and CRM platforms. Detailed integration specifications are shared during onboarding and preview engagements.",
    security:
      "SoftEbox is designed around secure system principles: encryption in transit and at rest, role-based access controls, and per-document audit logging. Specific certifications and independent audit reports are not yet claimed and will be published only when verified.",
    customers:
      "Designed for banks and financial institutions, utilities, telcos, insurance, public agencies and any organisation that must deliver documents with proof.",
    status: STATUS,
    screenshots: {
      recipient: {
        image: "/images/softebox-app-screenshot.png",
        label: "SoftEbox — Recipient / Mailbox View",
        caption: "Secure recipient digital mailbox interface and document delivery repository",
        isScreenshot: true,
      },
      admin: {
        image: "/images/softebox-admin-view.jpeg",
        label: "SoftEbox — Business Sender / Admin View",
        caption: "Business sender management console, document upload and dispatch tracking",
        isScreenshot: true,
      },
    },
  },
  {
    slug: "myid",
    name: "myID",
    tagline: "Digital Identity & Authentication",
    short: "Digital identity and authentication technology — biometric verification, device-bound credentials and enterprise integration.",
    desc: "myID is a digital identity and authentication technology initiative: a mobile-first way for people to prove who they are and for organisations to verify them — using biometrics, device-bound credentials and cross-device authentication.",
    logo: LOGOS.myid,
    logoBg: "#0e3b2e",
    darkBand: true,
    accent: "#1db954",
    useCase: "Enterprises hardening login and onboarding against fraud while keeping the experience effortless.",
    problem:
      "Passwords get phished, OTPs get intercepted, and manual KYC is slow and expensive. Organisations need strong, user-friendly authentication that binds an account to a real person and a verified device.",
    overview:
      "myID combines mobile authentication, biometric user verification and device-bound credentials into a single authentication layer. Users enrol once; after that they sign in, confirm high-risk actions and approve cross-device requests through simple, secure interactions.",
    how: [
      "Users enrol in the myID mobile app and complete biometric identity verification.",
      "A device-bound credential is created and secured on the user's device.",
      "The user authenticates with biometrics or the device credential.",
      "Cross-device logins are approved by scanning a QR code from a trusted device.",
      "The enterprise backend receives a signed, verifiable authentication result.",
    ],
    capabilities: [
      { title: "Mobile authentication", desc: "App-based sign-in that replaces passwords and SMS one-time codes.", status: "available" },
      { title: "Biometric user verification", desc: "Face and fingerprint verification bound to the enrolled identity.", status: "available" },
      { title: "Device-bound credentials", desc: "Credentials that cannot be copied between devices.", status: "available" },
      { title: "Cross-device QR authentication", desc: "Approve desktop logins from an enrolled phone via QR.", status: "available" },
      { title: "Authentication session management", desc: "Session lifecycle, step-up authentication and revocation controls.", status: "available" },
      { title: "Enterprise integration", desc: "SDKs and APIs for embedding myID into web and mobile services.", status: "available" },
      { title: "National identity database integrations", desc: "Connections to government identity systems.", status: "roadmap" },
    ],
    integration:
      "myID is designed for enterprise integration through SDKs and a verification API, supporting web login, mobile app sign-in, step-up verification for high-risk transactions and workforce access scenarios.",
    security:
      "myID is built on device-bound cryptography, biometric verification and signed authentication responses. myID is an independent product initiative by Softline; it is not affiliated with, endorsed by, or connected to any national identity system unless explicitly stated on this website.",
    customers:
      "Designed for banks and fintechs, digital platforms, telecoms, healthcare providers and public services that need strong customer authentication.",
    status: STATUS,
    screenshots: {
      recipient: {
        image: "/images/myid-app-screenshot.png",
        label: "myID — Recipient / Application View",
        caption: "Mobile identity enrolment, biometric credential and verification view",
        isScreenshot: true,
      },
      admin: {
        image: "/images/myid-admin-view.jpeg",
        label: "myID — Business / Admin View",
        caption: "Enterprise console: identity authentication, user activity log and fraud monitoring",
        isScreenshot: true,
      },
    },
  },
  {
    slug: "cofundapp",
    name: "CoFundApp",
    tagline: "Save Together, Grow Together",
    short: "Structured community savings technology — digitise group contributions, governance and payout coordination.",
    desc: "CoFundApp is a community savings technology platform that brings structure and transparency to group savings — the Nigerian esusu, ajo and adashi traditions — with scheduled contributions, clear governance and coordinated payouts.",
    logo: LOGOS.cofundapp,
    logoBg: "#0c1f18",
    darkBand: true,
    accent: "#0ea5b7",
    useCase: "Savings groups, cooperatives and communities replacing ledger books and WhatsApp reminders.",
    problem:
      "Community savings run on paper records, trust and group-chat reminders. Contributions get missed, records get disputed, payout order causes conflict, and organisers carry all the administrative burden with no audit trail.",
    overview:
      "CoFundApp digitises the full group savings cycle: groups define their rules, members join with identity verification, contributions are scheduled and tracked, payout rounds are coordinated transparently, and every transaction is recorded in a shared ledger.",
    how: [
      "An organiser creates a group and defines contribution amounts, frequency and payout rules.",
      "Members join and complete identity verification.",
      "Contributions are scheduled, recorded and tracked for every member.",
      "Payout rounds are coordinated according to the group's agreed order.",
      "The group ledger and member statements stay available to everyone, always.",
    ],
    capabilities: [
      { title: "Group savings management", desc: "Create and manage savings groups with clear, shared rules.", status: "available" },
      { title: "Contribution scheduling", desc: "Automated schedules, reminders and contribution tracking.", status: "available" },
      { title: "Member governance", desc: "Roles, approvals and dispute resolution recorded in the app.", status: "available" },
      { title: "Payout coordination", desc: "Transparent payout cycles with a shared, tamper-evident ledger.", status: "available" },
      { title: "Identity verification integration", desc: "Member verification to keep groups trusted and accountable.", status: "available" },
      { title: "Financial risk controls", desc: "Group-level limits, contribution health and default indicators.", status: "available" },
      { title: "Custody & payment rails", desc: "Regulated custody arrangements and payment processing.", status: "roadmap" },
    ],
    integration:
      "CoFundApp is designed to integrate with identity verification providers and, as the platform matures, with regulated payment and custody arrangements. Current deployments use CoFundApp's own coordination and record-keeping workflows.",
    security:
      "CoFundApp applies role-based access, verified member identity and a complete transaction ledger. CoFundApp provides record-keeping and coordination technology: it is not a bank, an escrow service or a licensed fund manager, and no claim of deposit safeguarding or regulated custody is made until such arrangements are executed and disclosed.",
    customers:
      "Designed for community savings groups, cooperatives, associations and organisations that run structured contribution programmes.",
    status: STATUS,
    screenshots: {
      recipient: {
        image: "/images/cofundapp-screenshot.jpg",
        label: "CoFundApp — Member / Community View",
        caption: "Community member savings overview and scheduled contribution tracking",
        isScreenshot: true,
      },
      admin: {
        image: "/images/cofundapp-admin-view.jpeg",
        label: "CoFundApp — Group Admin / Coordination View",
        caption: "Group administrator governance console and contribution ledger",
        isScreenshot: true,
      },
    },
  },
];

export const SERVICES = [
  {
    slug: "solution-architecture",
    num: "01",
    name: "Solution Architecture",
    short: "Enterprise and solution architecture that turns business ambition into buildable, governable technology roadmaps.",
    challenge:
      "Enterprises accumulate systems faster than they accumulate coherence. Without deliberate architecture, every project adds another integration point, another vendor lock-in and another source of operational risk.",
    proposition:
      "We design solution and enterprise architectures that make change safe: clear target states, pragmatic migration paths, integration blueprints and technical governance your teams can actually operate.",
    capabilities: [
      "Enterprise and solution architecture design",
      "Integration and interoperability blueprints",
      "Legacy modernisation and migration roadmaps",
      "Architecture governance, standards and review boards",
      "Technology selection and vendor-neutral evaluation",
      "Reference architectures for regulated industries",
    ],
    methodology:
      "Discovery and current-state assessment → target-state architecture → gap analysis and roadmap → governance setup → architecture review cycles that stay with delivery.",
    technologies: [
      "TOGAF-informed practices",
      "Enterprise integration patterns",
      "API-first and event-driven design",
      "Cloud-native reference architectures",
    ],
    deliverables: [
      "Current and target-state architecture descriptions",
      "Modernisation and migration roadmap",
      "Integration blueprint and standards",
      "Architecture decision records",
    ],
    industries: ["Financial services", "Government and public sector", "Enterprise and corporate"],
    products: ["softebox", "myid"],
  },
  {
    slug: "cloud-infrastructure",
    num: "02",
    name: "Cloud & Infrastructure",
    short: "Cloud assessment, migration, infrastructure automation and resilient operations across major public cloud platforms.",
    challenge:
      "Ageing infrastructure, rising hosting costs and fragile manual operations hold enterprises back. Moving to the cloud without assessment or automation simply relocates the problem — at a higher invoice.",
    proposition:
      "We assess, architect, migrate and operate infrastructure on major public cloud platforms — with infrastructure-as-code, resilience patterns, disaster recovery and cost discipline built in from day one.",
    capabilities: [
      "Cloud readiness assessment and landing zone design",
      "Migration planning and execution",
      "Infrastructure as code and environment automation",
      "High availability, resilience and disaster recovery",
      "Cost optimisation and FinOps practices",
      "Monitoring, observability and operations runbooks",
    ],
    methodology:
      "Assess → design landing zones and network topology → migrate workloads in waves → automate operations → review resilience, cost and security continuously.",
    technologies: [
      "Public cloud platforms including AWS, Microsoft Azure and Google Cloud",
      "Kubernetes and container platforms",
      "Terraform and CI/CD automation",
      "Linux and Windows server estates",
    ],
    deliverables: [
      "Cloud assessment report and target architecture",
      "Landing zone and migration plan",
      "Automated infrastructure codebase",
      "Disaster recovery and operations documentation",
    ],
    industries: ["Financial services", "Enterprise and corporate organizations", "Technology and digital platforms"],
    products: [],
  },
  {
    slug: "cybersecurity",
    num: "03",
    name: "Cybersecurity",
    short: "Security architecture, risk assessment, identity and access management and secure development practices.",
    challenge:
      "Fraud, ransomware and regulatory scrutiny are rising across Nigerian enterprises and public institutions. Security bought as a product stack — rather than designed into systems — leaves the risk where it started.",
    proposition:
      "We help organisations design security into their systems and operations: architecture and risk reviews, identity and access management, secure development practices and compliance advisory aligned to applicable regulations.",
    capabilities: [
      "Security architecture and system design reviews",
      "Risk assessment and treatment planning",
      "Identity and access management design",
      "Secure development lifecycle and DevSecOps practices",
      "Compliance advisory (NDPA 2023 and applicable standards)",
      "Security awareness and engineering enablement",
    ],
    methodology:
      "Understand the estate → assess risk → design controls into architecture → embed secure engineering practices → review and mature continuously.",
    technologies: [
      "IAM and authentication standards (OAuth 2.0, OpenID Connect, FIDO2)",
      "Encryption and key management practices",
      "Threat modelling and secure SDLC tooling",
      "Vulnerability management practices",
    ],
    deliverables: [
      "Risk assessment report and treatment plan",
      "Security architecture blueprint",
      "IAM design and implementation roadmap",
      "Secure development standards and training",
    ],
    industries: ["Financial services", "Government and public sector", "Healthcare"],
    products: ["myid"],
  },
  {
    slug: "software-engineering",
    num: "04",
    name: "Software Engineering",
    short: "Product-grade web, mobile and API engineering — from discovery through delivery, testing and modernisation.",
    challenge:
      "Off-the-shelf software rarely fits the business exactly, and legacy applications get more expensive to change every quarter. Teams need engineering capacity that ships production-quality software, not slide decks.",
    proposition:
      "We design and engineer digital products and enterprise applications: discovery, UX, frontend and backend engineering, APIs, cloud-native development, testing and modernisation of existing systems.",
    capabilities: [
      "Product discovery and application design",
      "Frontend and backend engineering",
      "API design and platform development",
      "Cloud-native application development",
      "Application modernisation and replatforming",
      "Automated testing and DevSecOps delivery",
    ],
    methodology:
      "Discover and specify → design and prototype → iterate in agile delivery increments → harden with automated testing and security review → release and support.",
    technologies: [
      "React, React Native and modern web stacks",
      "Python and Node.js backend services",
      "REST and event-driven APIs",
      "Automated testing and CI/CD pipelines",
    ],
    deliverables: [
      "Working product increments in production",
      "Documented, testable codebase",
      "API specifications and developer documentation",
      "Modernisation assessment and delivery plan",
    ],
    industries: ["Enterprise and corporate organizations", "Technology and digital platforms", "Education"],
    products: ["softebox", "myid", "cofundapp"],
  },
  {
    slug: "systems-integration",
    num: "05",
    name: "Systems Integration",
    short: "Enterprise APIs, messaging, middleware and secure data exchange that make separate systems work as one.",
    challenge:
      "Core banking platforms, ERPs, CRMs, identity systems and line-of-business apps all hold a piece of the truth. When they don't talk, staff re-key data, processes stall and audits fail.",
    proposition:
      "We integrate the systems you already run: enterprise APIs, application integration, identity systems, interoperability, messaging, middleware and secure data exchange — designed for reliability and auditability.",
    capabilities: [
      "Enterprise application integration (EAI)",
      "API design, gateway and management",
      "Identity systems integration",
      "Messaging, middleware and event streaming",
      "Secure data exchange and transformation",
      "Legacy system interoperability",
    ],
    methodology:
      "Map systems and data flows → design integration architecture → build and test interfaces → deploy with monitoring → document and hand over operating procedures.",
    technologies: [
      "API gateways and management platforms",
      "Message brokers and event streaming",
      "Middleware and ESB patterns",
      "Standards-based interoperability (HL7, ISO 20022 and domain formats)",
    ],
    deliverables: [
      "Integration architecture and data flow maps",
      "Working interfaces between systems",
      "Monitoring and failure-handling design",
      "Integration operations documentation",
    ],
    industries: ["Financial services", "Government and public sector", "Healthcare"],
    products: ["softebox", "myid"],
  },
  {
    slug: "data-digital-transformation",
    num: "06",
    name: "Data & Digital Transformation",
    short: "Data architecture, analytics enablement and workflow modernisation that turn paper processes into digital services.",
    challenge:
      "Data trapped in spreadsheets, silos and paper workflows cannot power decisions or digital services. Transformation programmes fail when they chase tools instead of fixing processes and data foundations first.",
    proposition:
      "We modernise how the organisation works: data architecture and integration, analytics enablement, workflow digitisation and process transformation — sequenced so value lands early and momentum compounds.",
    capabilities: [
      "Data architecture and platform design",
      "Data integration and quality management",
      "Analytics and reporting enablement",
      "Workflow digitisation and automation",
      "Digital process redesign",
      "Change enablement and adoption support",
    ],
    methodology:
      "Baseline processes and data → design the target operating model → integrate and clean data → digitise workflows in priority order → measure adoption and outcomes.",
    technologies: [
      "Modern data platforms and warehouses",
      "ETL/ELT and data quality tooling",
      "Business intelligence and reporting stacks",
      "Workflow and process automation platforms",
    ],
    deliverables: [
      "Data architecture and integration roadmap",
      "Digitised priority workflows in production",
      "Analytics and reporting capability",
      "Adoption and change management plan",
    ],
    industries: ["Government and public sector", "Financial services", "Education", "Healthcare"],
    products: ["cofundapp", "softebox"],
  },
];

export const INDUSTRIES = [
  {
    name: "Government & Public Sector",
    desc: "Digital services that stand up to scrutiny — secure citizen-facing platforms, document delivery, identity flows and systems integration across agencies.",
    useCases: ["Citizen notification and document delivery", "Secure authentication for public portals", "Agency systems interoperability"],
  },
  {
    name: "Financial Services",
    desc: "Banks, fintechs and insurers modernising onboarding, hardening authentication, and delivering statements and notices with provable delivery.",
    useCases: ["Strong customer authentication", "Statement and notice delivery with audit trails", "Fraud-resistant onboarding"],
  },
  {
    name: "Enterprise & Corporate",
    desc: "Large organisations consolidating systems, automating workflows and engineering the internal platforms their teams depend on.",
    useCases: ["ERP and line-of-business integration", "Workforce identity and access", "Internal product engineering"],
  },
  {
    name: "Healthcare",
    desc: "Provider and health-tech platforms that protect sensitive data while making patient and administrative journeys effortless.",
    useCases: ["Patient identity and access", "Secure document and records exchange", "Digital front desk workflows"],
  },
  {
    name: "Education",
    desc: "Institutions and edtech platforms modernising student services, credentials and administration on dependable digital foundations.",
    useCases: ["Student identity and portal access", "Digital delivery of transcripts and notices", "Administrative workflow digitisation"],
  },
  {
    name: "Technology & Digital Platforms",
    desc: "Digital-native companies that need senior engineering capacity, robust APIs and security-conscious architecture to scale.",
    useCases: ["Platform engineering and APIs", "Cloud-native architecture reviews", "Embedded product teams"],
  },
];

export const INSIGHTS = [
  {
    slug: "rethinking-document-delivery",
    title: "Why Nigerian enterprises are rethinking document delivery",
    category: "Digital Transformation",
    date: "2026-10-05",
    readTime: "4 min read",
    excerpt:
      "Email attachments and courier runs were never designed for compliance. Here is what changes when critical documents move through a structured digital mailbox instead.",
    body: [
      "Every month, Nigerian enterprises send millions of business-critical documents — statements, policies, invoices, statutory notices. Most still travel as email attachments or printed letters, and almost none of them come with proof of what happened next.",
      "The cost shows up quietly: call centres fielding 'I never received it' disputes, compliance teams reconstructing delivery trails by hand, and customers churning over a lost welcome pack.",
      "A structured digital mailbox inverts the model. The document is delivered to a verified recipient, its status is tracked at every step, and the audit trail is a by-product of the workflow rather than a project to reconstruct.",
      "In our work with enterprises, the pattern is consistent — the technology is rarely the hard part. The hard part is agreeing that delivery is a business event worth instrumenting.",
    ],
  },
  {
    slug: "authentication-people-actually-use",
    title: "Designing authentication that people actually use",
    category: "Identity & Security",
    date: "2026-09-18",
    readTime: "5 min read",
    excerpt:
      "Strong authentication fails in the real world when it fights the user. What device-bound credentials and biometrics get right — and what still gets in the way.",
    body: [
      "Security teams tend to grade authentication on entropy. Users grade it on seconds. A control that people routinely defeat — shared OTPs, written-down passwords, approved-anything push fatigue — is not strong, whatever the specification says.",
      "The most important shift of the last few years is the move from secrets to bindings: credentials that live in a device's secure hardware, unlocked by a biometric, and that cannot be copied between devices.",
      "Cross-device authentication deserves equal attention. The QR-based approval flow — sign in on the desktop, approve from the phone you enrolled — is now familiar enough to feel effortless and strong enough to resist phishing.",
      "The lesson we apply to myID: make the secure path the shortest path, and measure abandonment like a product team, not like an audit.",
    ],
  },
  {
    slug: "community-savings-digitised",
    title: "Community savings, digitised: what esusu groups need from software",
    category: "Fintech",
    date: "2026-08-30",
    readTime: "4 min read",
    excerpt:
      "Esusu, ajo, adashi — community savings are Nigeria's oldest fintech. What we learned building CoFundApp about translating trust into software.",
    body: [
      "Community savings groups have run for generations on two things: a ledger and trust. Any software that replaces the ledger without earning the trust will not be adopted — and that is exactly where most attempts fail.",
      "Building CoFundApp taught us that the essential features are unglamorous: contribution schedules that match real collection days, a payout order everyone can see, and a record of who agreed to what that survives a dispute.",
      "Identity verification matters more than payment rails in the early stage. Groups tell us the harder problem is knowing that the member joining is the person the group knows — not moving money yet.",
      "The roadmap follows the trust: structure first, payments and custody later, and only when the group's governance can carry them.",
    ],
  },
];

export const SEARCH_INDEX = [
  ...PRODUCTS.map((p) => ({
    title: `${p.name} — ${p.tagline}`,
    path: `/products/${p.slug}`,
    excerpt: p.short,
    keywords: [p.name, p.tagline, "product", ...p.short.toLowerCase().split(" ")],
  })),
  ...SERVICES.map((s) => ({
    title: s.name,
    path: `/services/${s.slug}`,
    excerpt: s.short,
    keywords: [s.name, "service", ...s.short.toLowerCase().split(" ")],
  })),
  ...INDUSTRIES.map((i) => ({
    title: i.name,
    path: "/industries",
    excerpt: i.desc,
    keywords: [i.name, "industry", ...i.desc.toLowerCase().split(" ")],
  })),
  ...INSIGHTS.map((i) => ({
    title: i.title,
    path: `/insights/${i.slug}`,
    excerpt: i.excerpt,
    keywords: [i.category.toLowerCase(), ...i.title.toLowerCase().split(" ")],
  })),
  { title: "About Us", path: "/about", excerpt: "Softline Technologies and Services Nigeria Limited — who we are, our mission, values and capabilities.", keywords: ["about", "company", "mission", "values", "nigeria", "softline"] },
  { title: "Our Group", path: "/about/our-group", excerpt: "The relationship between Softline Technologies and Services AB (Sweden) and Softline Technologies and Services Nigeria Limited.", keywords: ["group", "sweden", "parent", "subsidiary", "international"] },
  { title: "Contact", path: "/contact", excerpt: "Request a consultation, product preview or partnership conversation with the Softline Nigeria team.", keywords: ["contact", "consultation", "demo", "enquiry", "sales"] },
  { title: "Technology & Security", path: "/technology-security", excerpt: "How we engineer: secure system design, privacy and data protection, enterprise architecture, quality assurance and operational reliability.", keywords: ["security", "technology", "engineering", "privacy", "quality"] },
  { title: "Insights", path: "/insights", excerpt: "Perspectives on digital products, identity, document services and enterprise technology from the Softline Nigeria team.", keywords: ["insights", "blog", "articles", "news"] },
];

export function searchSite(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  return SEARCH_INDEX.map((entry) => {
    const haystack = `${entry.title} ${entry.excerpt} ${entry.keywords.join(" ")}`.toLowerCase();
    let score = 0;
    for (const t of terms) {
      if (entry.title.toLowerCase().includes(t)) score += 3;
      if (haystack.includes(t)) score += 1;
    }
    return { ...entry, score };
  })
    .filter((e) => e.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 12);
}

export const CONTACT_INFO = {
  regNo: "RC 8778015",
  offices: ["35, Lake Chad, Maitama, Abuja", "Abuja Conak Building, 17 Akinremi St, Anifowoshe, Ikeja, Lagos"],
  phones: ["+234 802 220 6266", "+1 (708) 433 9661"],
  email: "info@softlinetechs.com",
};
